import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { TablePaginationProps } from '@mui/material/TablePagination';
import { amber, green, red } from '@mui/material/colors';
import { FC, ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import ExportCsvButton from '@/components/buttons/export-csv-button';
import ExportExcelButton from '@/components/buttons/export-excel-button';
import { isWidthDown } from '@/hocs/with-width';
import { useWidth } from '@/hooks/use-width';

const useStyles = makeStyles()((theme) => ({
  disabledEm: {
    color: theme.palette.action.disabled,
  },
  '@keyframes pulseUrgencyCritical': {
    '0%': { backgroundColor: red['800'] },
    '50%': { backgroundColor: red['200'] },
    '100%': { backgroundColor: red['800'] },
  },
  '@keyframes pulseUrgencyMedium': {
    '0%': { backgroundColor: amber['600'] },
    '50%': { backgroundColor: amber['400'] },
    '100%': { backgroundColor: amber['600'] },
  },
  littlePadding: {
    [theme.breakpoints.down('md')]: {
      '& > *:first-child': {
        paddingLeft: theme.spacing(),
        paddingRight: theme.spacing(0.5),
      },
      '& > *, & > *:last-child': {
        paddingRight: theme.spacing(),
      },
    },
  },
  buttonIsPending: {
    backgroundColor: theme.palette.action.disabled,
  },
  buttonInProgress: {
    animationDuration: '3s !important',
  },
  buttonUrgencyCritical: {
    backgroundColor: red[700],
    animationName: '$pulseUrgencyCritical',
    animationIterationCount: 'infinite',
    animationDuration: '750ms',
  },
  backgroundUrgencyCritical: {
    backgroundColor: red[50],
  },
  buttonUrgencyMedium: {
    backgroundColor: amber[400],
    animationName: '$pulseUrgencyMedium',
    animationIterationCount: 'infinite',
    animationDuration: '750ms',
  },
  backgroundUrgencyMedium: {
    backgroundColor: amber[50],
  },
  backgroundPending: {
    backgroundImage:
      'repeating-linear-gradient(-45deg, #f9f7f7, #f9f7f7 10px, rgba(255, 255, 255, 0.075) 10px, rgba(255, 255, 255, 0.075) 20px)',
  },
  buttonResolved: {
    backgroundColor: green[400],
  },
  noWrap: {
    whiteSpace: 'nowrap',
  },
  table: {
    width: '100%',
    // tableLayout: 'fixed',
    overflow: 'auto',
  },
  truncate: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    maxWidth: '25vw',
  },
  stretch: {
    maxWidth: '50vw',
  },
  openInBrowser: {
    paddingRight: theme.spacing(),
    verticalAlign: 'text-top',
  },
  exportButtonsAndPagination: {
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
    [theme.breakpoints.down('md')]: {
      display: 'grid-inline',
    },
  },
  hideOnMDUp: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  hideOnSMDown: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
}));

type ColumnIdentifier = string;

export interface Header {
  key: ColumnIdentifier;
  label: string;
  align?: TableCellProps['align'];
  className?: string;
  hideWhileSmall?: boolean;
  width?: string | number;
}

export interface Cell {
  key: ColumnIdentifier;
  className?: string;
  children: ReactNode;
}

export interface Row {
  key: string;
  className?: string;
  cells: Cell[];
}

// FIXME: Store this elsewhere, it's generic to the CSV - maybe there's a
// @types/react-csv so we can remove it altogether?
interface CSVHeadersDataType {
  label: string;
  key: string;
}

interface Properties {
  rows: Row[];
  headers: Header[];
  // NOTE: As of this writing, this is currently not lazy and is being processed
  // at render time!
  onExport?: () => { headers: CSVHeadersDataType[]; data: Array<Record<string, string | number | Date>> };
}

const ExportablePaginated: FC<Properties> = (props) => {
  const { classes, cx } = useStyles();
  const [t] = useTranslation(['shared']);

  const {
    rows,
    headers,
    children = (
      <TableRow key="empty" className={cx(classes.littlePadding)}>
        <TableCell align="center" colSpan={headers.length}>
          <Typography variant="body2" align="center">
            {t(['shared:emptyTable'], { defaultValue: 'Table is empty.' })}
          </Typography>
        </TableCell>
      </TableRow>
    ),
    onExport,
  } = props;

  const { headers: exportHeaders, data: exportData } = onExport ? onExport() : { headers: [], data: [] };

  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);

  const width = useWidth();
  const isSmallScreen = isWidthDown('sm', width);
  const rowsPerPageOptions = isSmallScreen ? [] : [5, 10, 25];

  const tableContent = rows.length
    ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(({ key, cells, className }) => {
        return (
          <TableRow key={key} className={cx(className, classes.littlePadding)}>
            {cells.map((cell) => (
              <TableCell key={cell.key}>{cell.children}</TableCell>
            ))}
          </TableRow>
        );
      })
    : children;

  const onChangePage = useCallback<NonNullable<TablePaginationProps['onPageChange']>>((_, p) => setPage(p), []);
  const onChangeRowsPerPage = useCallback<NonNullable<TablePaginationProps['onRowsPerPageChange']>>(
    ({ target: { value } }) => {
      return setRowsPerPage(Number(value));
    },
    [],
  );

  return (
    <Grid container>
      <Table size={isSmallScreen ? 'small' : 'medium'} className={classes.table}>
        <TableHead>
          <TableRow className={classes.littlePadding}>
            {headers.map(({ key, align, label, className, width }) => {
              return (
                <TableCell key={key} align={align} className={className} style={{ width }}>
                  {label}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>{tableContent}</TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              colSpan={headers.length}
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
              component={({ children }) => (
                <TableCell colSpan={headers.length}>
                  <div className={classes.exportButtonsAndPagination}>
                    <Grid item sm={12} md={6} className={classes.hideOnSMDown}>
                      <ExportCsvButton
                        id="maintenanceCallsToCsv"
                        data={exportData}
                        headers={exportHeaders}
                        filename={'calls-controls'}
                      />
                      <ExportExcelButton
                        id="maintenanceCallsToExcel"
                        t={t}
                        data={[{ name: 'data', data: exportData }]}
                        headers={exportHeaders}
                        name={'MaintenanceCall'}
                      />
                    </Grid>
                    <Grid item sm={12} md={6}>
                      {children}
                    </Grid>

                    <Grid item sm={12} md={6} className={classes.hideOnMDUp}>
                      <ExportCsvButton
                        id="maintenanceCallsToCsv"
                        data={exportData}
                        headers={exportHeaders}
                        filename={'calls-controls'}
                      />
                      <ExportExcelButton
                        id="maintenanceCallsToExcel"
                        t={t}
                        headers={exportHeaders}
                        data={[{ name: 'data', data: exportData }]}
                        name={'MaintenanceCall'}
                      />
                    </Grid>
                  </div>
                </TableCell>
              )}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </Grid>
  );
};

export default ExportablePaginated;
